import { uniqueBy } from '~/utils/arrayUtils';

import {
  BASIC_CATEGORY_INDEX,
  SOLO_CATEGORY_INDEX,
  SOLO_UPGRADE_CATEGORY_INDEX,
  UPGRADE_CATEGORY_INDEX,
} from '../packageOptions/packageCategories';
import type { PackageOptionId, RoomPricing } from '../packageOptions/types';

import { computeOverrideCommission } from './computeOverrideCommission';
import type { OverrideCommissionBasis, OverrideCommissionTier } from './computeOverrideCommission';
import { computePackageSegmentation } from './computePackageSegmentation';
import { packageKey, parseMoney, round2 } from './forecastingModel';

export interface ForecastRoom {
  id: PackageOptionId;
  assetName: string;
  assetId: string;
  packageName?: string;
}

export interface ForecastOptionInput {
  roomsByCategory: Array<Array<ForecastRoom>>;
  pricingByRoomId: Record<PackageOptionId, RoomPricing>;
  lengthsOfStay: Array<number>;
  packagesByAssetLos: Record<string, number>;
  upgradeRatePct: number;
  soloRatePct: number;
  expectedAvgLos: number | null;
  // Units of contract currency per 1 AUD, 1 when the account contracts in AUD.
  audToContractRate: number;
  bundledCostAud?: number;
  commissionBasis?: OverrideCommissionBasis;
  tiers?: ReadonlyArray<OverrideCommissionTier>;
}

export interface UnpricedCell {
  assetName: string;
  nights: number;
}

export interface ForecastOptionTotals {
  packages: number;
  roomNights: number;
  avgLos: number | null;
  baseAvgLos: number | null;
  blendedAdrAud: number | null;
  blendedAdrContract: number | null;
  ttvAud: number;
  vendorRevenueAud: number;
  vendorRevenueContract: number;
  bundledCostAud: number;
  overrideCommsPct: number;
  overrideCommsAud: number;
  grossMarginAud: number;
  pctMargin: number | null;
  unpricedCells: Array<UnpricedCell>;
  unpricedExtraNightRooms: Array<string>;
}

const round1 = (value: number) => Math.round(value * 10) / 10;

const clampRatePct = (value: number) => Math.min(100, Math.max(0, Number(value) || 0));

const byAsset = (rooms: Array<ForecastRoom> = []) =>
  new Map(rooms.filter((room) => room.assetId).map((room) => [room.assetId, room]));

export function computeForecastTotals(input: ForecastOptionInput): ForecastOptionTotals {
  const {
    roomsByCategory,
    pricingByRoomId,
    lengthsOfStay,
    packagesByAssetLos,
    expectedAvgLos,
    audToContractRate,
    bundledCostAud = 0,
  } = input;

  // The caller withholds a non-positive rate, so there is no guard here.
  const netToAud = (net: number) => net / audToContractRate;

  // Packages are keyed by asset, so two base rows on one asset would each read the same cell and
  // double every total. The room picker prevents it; this keeps a slip from inflating the forecast.
  const basicRooms = uniqueBy(
    (roomsByCategory[BASIC_CATEGORY_INDEX] ?? []).filter((room) => room.assetId),
    (room) => room.assetId,
  );
  const upgradeByAsset = byAsset(roomsByCategory[UPGRADE_CATEGORY_INDEX]);
  const soloByAsset = byAsset(roomsByCategory[SOLO_CATEGORY_INDEX]);
  const soloUpgradeByAsset = byAsset(roomsByCategory[SOLO_UPGRADE_CATEGORY_INDEX]);

  let packages = 0;
  let roomNights = 0;
  let ttvAud = 0;
  let vendorRevenueAud = 0;
  const unpricedCells: Array<UnpricedCell> = [];
  const unpricedExtraNightNames = new Set<string>();

  // Spreading the extra nights over base rooms prices an upgrade package's extra nights at the base
  // rate while its core nights price at the upgrade rate, so each segment banks against its own room.
  const packagesByRoom = new Map<PackageOptionId, { room: ForecastRoom; count: number }>();

  const addSegment = (count: number, room: ForecastRoom | undefined, nights: number) => {
    if (!count || !room) {
      return;
    }

    const banked = packagesByRoom.get(room.id);
    packagesByRoom.set(room.id, { room, count: (banked?.count ?? 0) + count });

    const pricing = pricingByRoomId[room.id];
    if (!pricing) {
      return;
    }

    ttvAud += count * parseMoney(pricing.sellByLos[nights]);
    vendorRevenueAud += count * netToAud(parseMoney(pricing.netByLos[nights]));
  };

  const packagesFor = (assetId: string, nights: number) =>
    Math.max(0, Math.round(Number(packagesByAssetLos[packageKey(assetId, nights)]) || 0));

  for (const room of basicRooms) {
    const { assetId } = room;
    const upgradeRoom = upgradeByAsset.get(assetId);
    const soloRoom = soloByAsset.get(assetId);
    const soloUpgradeRoom = soloUpgradeByAsset.get(assetId);
    const upgradeRatePct = upgradeRoom ? clampRatePct(input.upgradeRatePct) : 0;
    const soloRatePct = soloRoom ? clampRatePct(input.soloRatePct) : 0;
    // Without a solo upgrade room those packages would price at nothing and vanish from revenue
    // while still counting room nights, so they stay with the plain solo segment instead.
    const soloUpgradeRatePct = soloUpgradeRoom ? clampRatePct(input.upgradeRatePct) : 0;

    for (const nights of lengthsOfStay) {
      const cellPackages = packagesFor(assetId, nights);
      if (!cellPackages) {
        continue;
      }

      packages += cellPackages;
      roomNights += cellPackages * nights;

      const basePricing = pricingByRoomId[room.id];
      if (
        !basePricing ||
        parseMoney(basePricing.sellByLos[nights]) <= 0 ||
        parseMoney(basePricing.netByLos[nights]) <= 0
      ) {
        unpricedCells.push({ assetName: room.assetName, nights });
      }

      const segments = computePackageSegmentation(cellPackages, upgradeRatePct, soloRatePct, soloUpgradeRatePct);
      addSegment(segments.baseOnly, room, nights);
      addSegment(segments.upgOnly, upgradeRoom, nights);
      addSegment(segments.soloBase, soloRoom, nights);
      addSegment(segments.soloUpg, soloUpgradeRoom, nights);
    }
  }

  const baseAvgLos = packages > 0 ? roomNights / packages : null;
  let avgLos = baseAvgLos;

  if (expectedAvgLos !== null && avgLos !== null && expectedAvgLos > avgLos) {
    const extraNightsTotal = (expectedAvgLos - avgLos) * packages;

    for (const { room, count } of packagesByRoom.values()) {
      const pricing = pricingByRoomId[room.id];

      if (!pricing || parseMoney(pricing.extraNightSell) <= 0 || parseMoney(pricing.extraNightNet) <= 0) {
        // An upgrade room carries its base room's asset name, so the package name tells them apart.
        unpricedExtraNightNames.add(room.packageName ?? room.assetName);
      }

      if (!pricing) {
        continue;
      }

      const roomExtraNights = extraNightsTotal * (count / packages);
      ttvAud += roomExtraNights * parseMoney(pricing.extraNightSell);
      vendorRevenueAud += roomExtraNights * netToAud(parseMoney(pricing.extraNightNet));
    }

    roomNights = expectedAvgLos * packages;
    avgLos = expectedAvgLos;
  }

  const vendorRevenueContract = vendorRevenueAud * audToContractRate;
  const { pct: overrideCommsPct, amountAud: overrideCommsAud } = computeOverrideCommission({
    basis: input.commissionBasis ?? 'vendorRevenue',
    roomNights,
    tiers: input.tiers,
    vendorRevenueAud,
    vendorRevenueContract,
  });

  const grossMarginAud = ttvAud - vendorRevenueAud - bundledCostAud + overrideCommsAud;
  const blendedAdrAud = roomNights > 0 ? vendorRevenueAud / roomNights : null;

  return {
    avgLos: avgLos === null ? null : round2(avgLos),
    baseAvgLos: baseAvgLos === null ? null : round2(baseAvgLos),
    blendedAdrAud: blendedAdrAud === null ? null : round2(blendedAdrAud),
    blendedAdrContract: blendedAdrAud === null ? null : round2(blendedAdrAud * audToContractRate),
    bundledCostAud: round2(bundledCostAud),
    grossMarginAud: round2(grossMarginAud),
    overrideCommsAud: round2(overrideCommsAud),
    overrideCommsPct,
    packages,
    pctMargin: ttvAud > 0 ? round1((grossMarginAud / ttvAud) * 100) : null,
    roomNights: round2(roomNights),
    ttvAud: round2(ttvAud),
    unpricedCells,
    unpricedExtraNightRooms: [...unpricedExtraNightNames],
    vendorRevenueAud: round2(vendorRevenueAud),
    vendorRevenueContract: round2(vendorRevenueContract),
  };
}
